/**
 * WebSocket-based MultiplayerApi implementation.
 * Works in all environments (playground, production, etc.) — the join ticket
 * provider is injected via constructor config.
 */

import type { MatchmakeOptions, MultiplayerApi } from './MultiplayerApi'
import type { JoinTicketRequest, ListUserRoomsOptions, RealtimeRoomSummary, RoomOptions } from '../shared'
import { RundotServerRoom } from './RundotServerRoom'
import type { ServerRoom } from './ServerRoom'
import type { Protocol } from '../shared'
import { ServerWebSocket } from './ServerWebSocket'
import { PROTOCOL_VERSION, type ClientAuthMessage, type ServerToClientMessage } from './ServerProtocol'
import { maybeLogPlaygroundDuplicateSessionHint } from './playgroundDuplicateSessionHint'

export type { JoinTicketRequest } from '../shared'

export class WsMultiplayerApi implements MultiplayerApi {
  private readonly serverUrl: string | (() => string)
  private readonly getJoinTicket: (req: JoinTicketRequest) => Promise<string>
  private readonly listUserRooms?: (options?: ListUserRoomsOptions) => Promise<RealtimeRoomSummary[]>

  constructor(config: {
    serverUrl: string | (() => string)
    getJoinTicket: (req: JoinTicketRequest) => Promise<string>
    /** Host RPC that queries the `rundot_rooms` index (mirrors the ticket RPC). */
    listUserRooms?: (options?: ListUserRoomsOptions) => Promise<RealtimeRoomSummary[]>
  }) {
    this.serverUrl = config.serverUrl
    this.getJoinTicket = config.getJoinTicket
    this.listUserRooms = config.listUserRooms
  }

  private resolveServerUrl(): string {
    return typeof this.serverUrl === 'function' ? this.serverUrl() : this.serverUrl
  }

  async createRoom<P extends Protocol>(roomType: string, opts?: RoomOptions): Promise<ServerRoom<P>> {
    return this._joinWithAction<P>('create', roomType, undefined, opts)
  }

  async joinRoomByCode<P extends Protocol>(code: string): Promise<ServerRoom<P>> {
    return this._joinWithAction<P>('joinByCode', '', code)
  }

  async joinOrCreateRoom<P extends Protocol>(roomType: string, opts?: RoomOptions): Promise<ServerRoom<P>> {
    return this._joinWithAction<P>('joinOrCreate', roomType, undefined, opts)
  }

  async matchmakeRoom<P extends Protocol>(roomType: string, opts?: MatchmakeOptions): Promise<ServerRoom<P>> {
    const req: JoinTicketRequest = {
      roomType,
      action: 'matchmake',
      criteria: opts?.criteria,
    }
    const ticket = await this.getJoinTicket(req)
    return this._connectMatchmaking<P>(
      { type: 'auth', protocolVersion: PROTOCOL_VERSION, ticket, roomType, action: 'matchmake' },
      opts,
    )
  }

  async getUserRooms(options?: ListUserRoomsOptions): Promise<RealtimeRoomSummary[]> {
    // Runtime guard instead of a required constructor field: some hosts
    // (e.g. the Vite dev server until WS5 lands) construct this API without
    // a listing RPC, and must still satisfy the MultiplayerApi interface.
    if (!this.listUserRooms) {
      throw new Error('getUserRooms is not available in this environment')
    }
    return this.listUserRooms(options)
  }

  private async _joinWithAction<P extends Protocol>(
    action: 'create' | 'joinOrCreate' | 'joinByCode',
    roomType: string,
    roomCode?: string,
    opts?: RoomOptions,
  ): Promise<ServerRoom<P>> {
    const req: JoinTicketRequest = {
      roomType,
      action,
      roomCode,
      criteria: opts?.criteria,
      createOptions: opts?.createOptions,
      persistentKey: opts?.persistentKey,
    }
    const ticket = await this.getJoinTicket(req)
    return this._connect<P>(
      { type: 'auth', protocolVersion: PROTOCOL_VERSION, ticket, roomType, action, roomCode },
      opts,
    )
  }

  /**
   * Matchmaking connect (Req 18): like {@link _connect} but the socket parks in
   * the server lobby until paired. While the server reports `matchmaking:pending`
   * the client polls the pool; once the server pairs and emits `room:joined` the
   * room resolves. The waiting window is `matchmakeTimeoutMs` (default 120s),
   * deliberately longer than the plain connect timeout.
   */
  private _connectMatchmaking<P extends Protocol>(authMsg: ClientAuthMessage, opts?: MatchmakeOptions): Promise<ServerRoom<P>> {
    const wsUrl = this.resolveServerUrl().replace(/^http/, 'ws') + '/ws'
    const ws = new ServerWebSocket({ url: wsUrl })
    ws.setTicketProvider(this.getJoinTicket)
    ws.setJoinOptions({ criteria: opts?.criteria })

    const timeoutMs = opts?.matchmakeTimeoutMs ?? 120000
    const pollIntervalMs = opts?.pollIntervalMs ?? 1000

    return new Promise((resolve, reject) => {
      let resolved = false
      let pollTimer: ReturnType<typeof setInterval> | null = null

      const stopPolling = (): void => {
        if (pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
      }
      const startPolling = (): void => {
        if (pollTimer) return
        pollTimer = setInterval(() => {
          ws.send({ type: 'message', msgType: 'matchmaking:poll', data: {} })
        }, pollIntervalMs)
      }

      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true
          stopPolling()
          // Best-effort: tell the server to drop us from the pool before closing.
          ws.send({ type: 'message', msgType: 'matchmaking:cancel', data: {} })
          ws.close()
          reject(new Error('Matchmaking timeout — no opponent found'))
        }
      }, timeoutMs)

      const settle = (fn: () => void): void => {
        if (resolved) return
        resolved = true
        stopPolling()
        clearTimeout(timeout)
        fn()
      }

      const handlePreJoinMessage = (msg: ServerToClientMessage): void => {
        if (resolved) return
        switch (msg.type) {
          case 'room:joined': {
            ws.setAuthMessage({ ...authMsg, _roomId: msg._roomId })
            const room = new RundotServerRoom<P>(msg, ws)
            settle(() => resolve(room))
            break
          }
          case 'matchmaking:pending':
            // Enqueued — keep polling until paired.
            startPolling()
            break
          case 'matchmaking:idle':
            // Server reports we're no longer in the pool (expired or cancelled
            // out from under us). There is no re-enqueue path, so polling can't
            // recover — fail fast instead of hanging until the matchmake timeout.
            ws.close()
            settle(() => reject(new Error('Matchmaking is no longer active (pool expired or cancelled)')))
            break
          case 'matchmaking:cancelled':
            // Our cancel was acknowledged — stop waiting.
            ws.close()
            settle(() => reject(new Error('Matchmaking cancelled')))
            break
          case 'room:error':
            maybeLogPlaygroundDuplicateSessionHint(msg.code)
            ws.close()
            settle(() => reject(new Error(msg.message)))
            break
        }
      }

      const handlePreJoinError = (err: Error): void => {
        if (!resolved) {
          ws.close()
          settle(() => reject(err))
        }
      }

      ws.on({
        onMessage: handlePreJoinMessage,
        onError: handlePreJoinError,
        onStateChange: () => {},
      })

      ws.setAuthMessage(authMsg)
      ws.connect()
    })
  }

  private _connect<P extends Protocol>(authMsg: ClientAuthMessage, opts?: RoomOptions): Promise<ServerRoom<P>> {
    const wsUrl = this.resolveServerUrl().replace(/^http/, 'ws') + '/ws'
    const ws = new ServerWebSocket({ url: wsUrl })
    ws.setTicketProvider(this.getJoinTicket)
    ws.setJoinOptions({ criteria: opts?.criteria, createOptions: opts?.createOptions, persistentKey: opts?.persistentKey })

    return new Promise((resolve, reject) => {
      let resolved = false

      // Timeout after 15 seconds
      const timeout = setTimeout(() => {
        if (!resolved) {
          console.error(`[WsMultiplayerApi] connection timeout (15s)`)
          resolved = true
          ws.close()
          reject(new Error('Connection timeout'))
        }
      }, 15000)

      const settle = (fn: () => void) => {
        if (resolved) return
        resolved = true
        clearTimeout(timeout)
        fn()
      }

      const handlePreJoinMessage = (msg: ServerToClientMessage) => {
        if (resolved) return

        if (msg.type === 'room:joined') {
          ws.setAuthMessage({ ...authMsg, _roomId: msg._roomId })
          const room = new RundotServerRoom<P>(msg, ws)
          settle(() => resolve(room))
        } else if (msg.type === 'room:error') {
          maybeLogPlaygroundDuplicateSessionHint(msg.code)
          console.error(`[WsMultiplayerApi] room:error code=${msg.code} message=${msg.message}`)
          ws.close()
          settle(() => reject(new Error(msg.message)))
        }
      }

      const handlePreJoinError = (err: Error) => {
        console.error(`[WsMultiplayerApi] onError`, err)
        if (!resolved) {
          ws.close()
          settle(() => reject(err))
        }
      }

      const handleStateChange = (_state: string) => {}

      ws.on({
        onMessage: handlePreJoinMessage,
        onError: handlePreJoinError,
        onStateChange: handleStateChange,
      })

      ws.setAuthMessage(authMsg)
      ws.connect()
    })
  }
}
